/* eslint-disable react/no-unescaped-entities */
import { useCallback, useEffect, useRef } from 'react';
import { Spinner, Typography } from '@tempmee/react';
import { useDispatch } from 'react-redux';
import { FormProvider } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import { setDraftCount, setHasPostings } from '~/actions/PermanentJobs/PermanentJobs';
import { colors } from '@tempmee/tailwind';
import { Box, Button, Center, Column, Row } from '~/commonComponents';
import { EnforcePostAJobFunnelImprovements } from '~/constants/ff.posthog';
import { useCreateDraftJobPost, useSaveJobPostDetail } from '~/domain/PermanentJobs';
import { useAppSelector } from '~/hooks';
import { showErrorMessage } from '~/domain/utils';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { useUserSelector } from '~/hooks/useUserSelector';
import { useGetProfessionalCountsByOffice } from '~/domain/Professionals/usecases/useGetProfessionalCountsByOffice';
import { firePermPostJobBasicInfoSubmitted } from '~/posthogs/events';
import {
  AutoSaveStatus,
  BasicInfoForm,
  GeneralErrorMessage,
  InfoSection,
  JobDetailsFormMapper,
  useJobDetailsForm,
} from '../JobDetailsForm';
import {
  useAutoSaveDraftJobPost,
  useAutoScrollToSection,
  useDraftJobPost,
  useSaveJobPost,
} from './hooks';
import {
  CreatePermanentJobPostingFormSection,
  CreatePermanentJobPostingSteps,
  TCreatePermanentJobPostingPageRoutes,
} from './Types';

export const BasicInformationPage = () => {
  const { id } = useParams<TCreatePermanentJobPostingPageRoutes>();
  const isCreating = !id;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const draftCount = useAppSelector((state) => state.permanentJobs.draftCount);

  const { draft, isLoading } = useDraftJobPost(id);
  const isEnforcePostAJobFunnelImprovement = useFeatureFlag(EnforcePostAJobFunnelImprovements);

  const user = useUserSelector();
  const { totalCount } = useGetProfessionalCountsByOffice(user?.id, {
    enable: isEnforcePostAJobFunnelImprovement,
    distance: 25,
  });

  const generalJobInfoRef = useRef<HTMLDivElement>(null);

  const controller = useJobDetailsForm({
    initialValues: draft,
  });

  useEffect(() => {
    if (draft) {
      controller.reset(JobDetailsFormMapper.fromEntity(draft));
    }
  }, [draft, controller]);

  useAutoScrollToSection({
    sectionRefs: {
      [CreatePermanentJobPostingFormSection.GeneralJobInformation]: generalJobInfoRef,
    },
    isReady: !isLoading && !!draft,
  });

  const { isPending, hasFormErrors, onSave } = useSaveJobPost(controller, id, {
    targetStep: CreatePermanentJobPostingSteps.WorkDetails,
    triggers: ['jobTitle', 'office', 'officeSpecialty', 'professionType'],
    onTrackSuccess: (postingId, formData) => {
      firePermPostJobBasicInfoSubmitted({
        postingId,
        professionType: formData.professionType,
        professionalCount: totalCount,
      });
    },
  });

  const {
    isLoading: isAutoSaveLoading,
    isShowSavedMessage,
    lastSavedAt,
  } = useAutoSaveDraftJobPost(controller, id);

  const createDraftJobPost = useCreateDraftJobPost();
  const saveJobPostDetail = useSaveJobPostDetail();
  const isCreatingPending = createDraftJobPost.isPending || saveJobPostDetail.isPending;

  const onSaveAndCreateDraft = useCallback(async () => {
    const isValid = await controller.trigger(['professionType', 'jobTitle', 'officeSpecialty']);

    if (!isValid) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    createDraftJobPost.mutate(undefined, {
      onSuccess: (data) => {
        if (!data?.id) return;

        const fullPayload = JobDetailsFormMapper.toSaveJobPostDetailPayload(controller.getValues());
        const payload = {
          title: fullPayload.title,
          professionType: fullPayload.professionType,
          subProfession: fullPayload.subProfession,
          officeSpecialty: fullPayload.officeSpecialty,
        };
        saveJobPostDetail.mutate(
          { id: data.id, payload },
          {
            onSuccess: () => {
              dispatch(setHasPostings(true));
              dispatch(setDraftCount((draftCount ?? 0) + 1));
              firePermPostJobBasicInfoSubmitted({
                postingId: data.id,
                professionType: controller.getValues().professionType,
                professionalCount: totalCount,
              });
              const basePath = `/permanent-jobs/postings/${data.id}/create`;
              navigate(
                `${basePath}/${CreatePermanentJobPostingSteps.BasicInformation}`,
                { replace: true },
              );
              navigate(
                `${basePath}/${CreatePermanentJobPostingSteps.WorkDetails}`,
              );
            },
            onError: (err) => showErrorMessage({ error: err }),
          },
        );
      },
      onError: (err) => showErrorMessage({ error: err }),
    });
  }, [
    controller,
    createDraftJobPost,
    saveJobPostDetail,
    dispatch,
    navigate,
    totalCount,
    draftCount,
  ]);

  const handleSave = isCreating ? onSaveAndCreateDraft : onSave;
  const handleIsPending = isCreating ? isCreatingPending : isPending || isAutoSaveLoading;

  if (isLoading) {
    return (
      <Center marginBlock={48}>
        <Spinner centered variant="default" size="md" />
      </Center>
    );
  }

  if (isEnforcePostAJobFunnelImprovement) {
    return (
      <>
        {!isCreating && <AutoSaveStatus visible={isShowSavedMessage} date={lastSavedAt} />}
        <Column alignItems="center" gap={16}>
          <Column alignItems="center" gap={10}>
            <Typography as="span" bold size="heading_2" color={colors.neutral[600]} textAlign="center">
              Let's See Who's In Your Area
            </Typography>
            <Typography as="span" size="body_large" color={colors.neutral[500]} textAlign="center" style={{ maxWidth: 633 }}>
              Tell us what you're hiring for and we'll show you how many professionals are near you.
            </Typography>
          </Column>
          {hasFormErrors && <GeneralErrorMessage />}
          <Box width={800}>
            <FormProvider {...controller}>
              <InfoSection
                ref={generalJobInfoRef}
                title="Job Information"
                titleFontSize={24}
                titleColor="neutral_600"
              >
                <BasicInfoForm />
              </InfoSection>
            </FormProvider>
          </Box>
          <Row justifyContent="center" marginTop={24}>
            <Button
              text="Find Out Now"
              onClick={handleSave}
              isLoading={handleIsPending}
            />
          </Row>
        </Column>
      </>
    );
  }

  return (
    <>
      {!isCreating && <AutoSaveStatus visible={isShowSavedMessage} date={lastSavedAt} />}
      <Column width="60%" alignSelf="center">
        <Typography as="span" bold size="heading_4" style={{ marginBottom: 14, marginLeft: 24 }}>
          Step 1: Basic Information
        </Typography>
        {hasFormErrors && <GeneralErrorMessage />}
        <FormProvider {...controller}>
          <InfoSection
            ref={generalJobInfoRef}
            icon="briefcase-solid"
            title="General Job Information"
          >
            <BasicInfoForm />
          </InfoSection>
        </FormProvider>
        <Row justifyContent="center" marginTop={24}>
          <Button text="Next" onClick={handleSave} isLoading={handleIsPending} />
        </Row>
      </Column>
    </>
  );
};
